import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Pencil, Users } from 'lucide-react';
import api from '@/lib/api';
import { usePode } from '@/stores/authStore';
import Preloader from '@/components/Preloader';
import LogoFornecedor from '@/components/LogoFornecedor';
import type { Fornecedor } from './tipos';

/** Os três fornecedores das cotações de cabos, com o que o envio da cotação vai precisar. */
export default function FornecedoresPage() {
  const podeEditar = usePode('cadastro.fornecedores');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['fornecedores'],
    queryFn: async () => (await api.get<{ data: Fornecedor[] }>('/fornecedores')).data.data,
  });

  if (isLoading) {
    return <Preloader telaCheia mensagem="Carregando fornecedores…" className="py-3xl" />;
  }

  if (isError || !data) {
    return (
      <p className="px-lg py-lg text-[14px] text-danger">
        Não foi possível carregar os fornecedores. Recarregue a página para tentar de novo.
      </p>
    );
  }

  return (
    <div className="px-lg py-lg">
      <h1 className="text-[18px] font-bold text-ink">Fornecedores</h1>
      <p className="text-[13px] text-muted">
        Dados da empresa, logomarca e os contatos que recebem as cotações.
      </p>

      <div className="mt-lg grid gap-md sm:grid-cols-2 lg:grid-cols-3">
        {data.map((fornecedor) => {
          const recebem = fornecedor.contatos.filter((contato) => contato.recebe_cotacao).length;

          return (
            <section key={fornecedor.id} className="flex flex-col rounded-lg border border-border bg-surface p-md">
              <div className="flex h-[64px] items-center">
                {fornecedor.logo_url ? (
                  <LogoFornecedor logoUrl={fornecedor.logo_url} apelido={fornecedor.apelido} tamanho="lg" />
                ) : (
                  <span className="flex size-[56px] items-center justify-center rounded-full bg-accent/10 text-[22px] font-bold text-accent">
                    {fornecedor.apelido.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              <h2 className="mt-sm text-[16px] font-bold text-ink">{fornecedor.apelido}</h2>
              <p className="text-[13px] text-ink-soft">{fornecedor.nome}</p>
              <p className="text-[12px] tabular-nums text-muted">{fornecedor.cnpj ? `CNPJ ${fornecedor.cnpj}` : 'CNPJ não informado'}</p>

              <p className={`mt-sm inline-flex items-center gap-xs text-[12px] font-bold ${recebem ? 'text-ink-soft' : 'text-warning'}`}>
                {recebem ? <Users size={13} aria-hidden="true" /> : <AlertTriangle size={13} aria-hidden="true" />}
                {recebem === 0
                  ? 'Nenhum contato recebe cotação'
                  : recebem === 1
                    ? '1 contato recebe cotação'
                    : `${recebem} contatos recebem cotação`}
              </p>

              {podeEditar && (
                <Link
                  to={`/fornecedores/${fornecedor.id}`}
                  className="mt-md inline-flex items-center justify-center gap-xs rounded-md border border-accent bg-accent/10 px-sm py-sm text-[13px] font-bold text-accent hover:bg-accent hover:text-white"
                >
                  <Pencil size={14} aria-hidden="true" />
                  Editar
                </Link>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
